/**
 * @file BillPoPicker.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-03
 * @modified 2026-10-08
 * @description Choose the purchase orders a supplier's bill covers — PO-D,
 *              PO-H and Xero-made PO- alike — on Add Bill.
 *
 * Lists the supplier's POs (GET /api/bills/po-candidates: its account, Xero
 * contact or supplier group; never archived ones), newest delivery first. A
 * weekly haulage bill is the case it is built for: tick the week's PO-Hs and
 * each becomes a line for the haulier's figure to be split across. "Include
 * billed" shows POs already billed, for a partial or second bill — the save
 * then asks to confirm.
 *
 * 1.1.0 (2026-10-08): `title`, `addLabel` and `initialIncludeBilled` — Add
 * Supplier Credit uses it to choose the POs a credit relates to (a reference
 * only; billed POs shown from the start, since a credit usually follows the
 * bill).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import TickCheckbox from './TickCheckbox';
import LoadingSpinner from './LoadingSpinner';
import SearchHelp from './SearchHelp';
import { useSlideOutButton } from './SlideOutButton';
import useLatestRequest from '../hooks/useLatestRequest';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDateShort } from '../utils/dateFormatters';
import './BillPoPicker.css';

/** The body, mounted only while open, so the footer's slide-outs measure with it. */
function PickerBody({ rows, loading, error, selected, toggle, toggleAll, search, setSearch, includeBilled, setIncludeBilled, linked }) {
  useSlideOutButton([selected.size, loading]);
  const selectable = rows.filter(r => !linked.has(r.id));
  const allOn = selectable.length > 0 && selectable.every(r => selected.has(r.id));
  return (
    <div className="bill-po-picker-body">
      <div className="bill-po-picker-filters">
        <div className="filter-group">
          <label>Search <SearchHelp fields={['PO number', 'customer', 'order no.']} amounts /></label>
          <Input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="PO-H10022, Smith, 453.81…" />
        </div>
        <div className="filter-group">
          <label>Include billed</label>
          <div className="checkbox-container">
            <div className="check light">
              <input id="bill-po-picker-billed" type="checkbox" checked={includeBilled} onChange={(e) => setIncludeBilled(e.target.checked)} />
              <label htmlFor="bill-po-picker-billed"></label>
            </div>
          </div>
        </div>
      </div>
      {error && <p className="bill-po-picker-error">{error}</p>}
      {loading ? <LoadingSpinner /> : (
        <div className="table-container bill-po-picker-table">
          <table className="data-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <TickCheckbox id="bill-po-all" checked={allOn} onChange={toggleAll} disabled={!selectable.length} ariaLabel="Select every PO shown" />
                </th>
                <th>PO</th>
                <th>Kind</th>
                <th>Delivery</th>
                <th>Customer</th>
                <th className="currency">Total</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan="7" className="no-results">No open purchase orders for this supplier{includeBilled ? '' : ' (tick Include billed for billed ones)'}.</td></tr>
              )}
              {rows.map(r => {
                const isLinked = linked.has(r.id);
                const others = Array.isArray(r.other_bills) ? r.other_bills : [];
                return (
                  <tr key={r.id} className={isLinked ? 'is-linked' : undefined} onClick={() => !isLinked && toggle(r.id)}>
                    <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                      <TickCheckbox id={`bill-po-${r.id}`} checked={isLinked || selected.has(r.id)} disabled={isLinked}
                        onChange={() => toggle(r.id)} ariaLabel={`Choose ${r.purchase_order_number}`}
                        title={isLinked ? 'Already on this bill' : undefined} />
                    </td>
                    <td><strong>{r.purchase_order_number}</strong></td>
                    <td>{r.kind}</td>
                    <td>{formatDateShort(r.delivery_date || r.date)}</td>
                    <td>{r.customer_name || r.customer_order_number || '—'}</td>
                    <td className="currency">{formatCurrency(r.total)}</td>
                    <td>
                      <span className={`status-badge status-${String(r.status || '').toLowerCase()}`}>{r.status}</span>
                      {others.length > 0 && (
                        <span className="bill-po-picker-billed" title={others.map(o => o.supplier_reference || `bill #${o.id}`).join(', ')}>
                          on {others.length} bill{others.length === 1 ? '' : 's'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BillPoPicker({ isOpen, supplierId, xeroContactId, supplierName, excludeBillId = null, linkedIds = [], onClose, onAdd,
  title = null, addLabel = null, initialIncludeBilled = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [includeBilled, setIncludeBilled] = useState(!!initialIncludeBilled);
  const [selected, setSelected] = useState(() => new Set());
  const latest = useLatestRequest();
  const linked = useMemo(() => new Set((linkedIds || []).map(Number)), [linkedIds]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setSelected(new Set());
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !supplierId) return undefined;
    const t = setTimeout(async () => {
      const ticket = latest.begin();
      setLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams({ account_id: String(supplierId), include_billed: includeBilled ? 'true' : 'false' });
        if (xeroContactId) qs.set('xero_contact_id', xeroContactId);
        if (excludeBillId) qs.set('exclude_bill_id', String(excludeBillId));
        if (search.trim()) qs.set('search', search.trim());
        const res = await fetch(`${API_URL}/api/bills/po-candidates?${qs}`, { credentials: 'include' });
        const data = await res.json();
        if (!latest.isLatest(ticket)) return;
        if (!res.ok || !data.success) throw new Error(data.error || 'Could not load the POs');
        setRows(data.purchaseOrders || []);
      } catch (e) {
        if (latest.isLatest(ticket)) setError(e.message);
      } finally {
        if (latest.isLatest(ticket)) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, supplierId, xeroContactId, excludeBillId, search, includeBilled]);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleAll = () => setSelected(prev => {
    const selectable = rows.filter(r => !linked.has(r.id));
    const allOn = selectable.length > 0 && selectable.every(r => prev.has(r.id));
    return allOn ? new Set() : new Set(selectable.map(r => r.id));
  });

  const chosen = rows.filter(r => selected.has(r.id));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${title || 'Choose POs'}${supplierName ? ` — ${supplierName}` : ''}`}
      size="xl"
      className="bill-po-picker"
      footer={
        <div className="buttons slideout">
          <div>
            <button type="button" className="btn-slideout" onClick={onClose}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button type="button" className="btn-slideout btn-slideout-primary" disabled={!chosen.length} onClick={() => onAdd(chosen)}>
              <div className="icon-wrapper"><span className="material-symbols-rounded">playlist_add</span></div>
              <span className="btn-label">{chosen.length ? `${addLabel || 'Add'} ${chosen.length} PO${chosen.length === 1 ? '' : 's'}` : `${addLabel || 'Add'} POs`}</span>
            </button>
          </div>
        </div>
      }
    >
      <PickerBody
        rows={rows} loading={loading} error={error} selected={selected} toggle={toggle} toggleAll={toggleAll}
        search={search} setSearch={setSearch} includeBilled={includeBilled} setIncludeBilled={setIncludeBilled}
        linked={linked}
      />
    </Modal>
  );
}

export default BillPoPicker;
